import React, {useEffect, useState, useRef, type JSX} from 'react'
import {Helmet} from 'react-helmet'
import {clsx} from 'clsx'
import {pushToDataLayer} from '@/utils/analytics'
import {useTrustArcConsent} from '@/_new-code/services/trustarc'

interface WistiaProps {
	alt?: string
	autoPlay?: boolean
	className?: string
	id: string
	componentSpacing?: string
	spacing?: {
		default: string
		top: string
		bottom: string
		none: string
	}
	wistiaPodcast?: {choice: string}
	playSuspendedOffScreen?: boolean
	isActiveSlide?: boolean // prop to check if video is the active carousel slide
}
interface WistiaVideoObject {
	time: () => number
	duration: () => number
	bind: (name: string, callback: (event: Event) => void) => void
	pause: () => void
	play: () => void
}

const WistiaVideoDefault: WistiaVideoObject = {
	time: () => 0,
	duration: () => 0,
	bind: () => undefined,
	pause: () => undefined,
	play: () => undefined,
}

export const Wistia = ({
	alt = '',
	autoPlay = false,
	className = '',
	id,
	componentSpacing,
	spacing,
	wistiaPodcast,
	playSuspendedOffScreen = true,
	isActiveSlide = true,
}: WistiaProps): JSX.Element => {
	const videoRef = useRef<WistiaVideoObject>(WistiaVideoDefault)
	const progressTargetsRef = useRef([10, 25, 50, 75])
	const startLogged = useRef(false)
	const endLogged = useRef(false)
	const pausedByVisibility = useRef(false)
	const cookieConsent = useTrustArcConsent()
	const [tracking, setTracking] = useState(false)
	useEffect(() => {
		if (
			cookieConsent === 'ADVERTISE_COOKIES' ||
			cookieConsent === 'ALL_COOKIES'
		) {
			setTracking(true)
		} else {
			setTracking(false)
		}
	}, [cookieConsent])

	const onPlayHandler = (): void => {
		if (!startLogged.current) {
			pushToDataLayer({
				event: 'video_start',
				video_title: `${alt || 'No alt text set'} (wistia id: ${id})`,
				video_url: `https://elancoanimalhealth.wistia.com/medias/${id}`,
			})
			startLogged.current = true
		}
	}

	const onEndHandler = (): void => {
		if (!endLogged.current) {
			pushToDataLayer({
				event: 'video_complete',
				video_percent: '100%',
				video_title: `${alt} (wistia id: ${id})`,
				video_url: `https://elancoanimalhealth.wistia.com/medias/${id}`,
				video_duration: videoRef.current.duration(),
			})
			endLogged.current = true
		}
	}

	const onReady = (vid: WistiaVideoObject): void => {
		videoRef.current = vid
		vid.bind('play', onPlayHandler)
		vid.bind('end', onEndHandler)
	}

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ensure Wistia global queue is initialized before pushing items
		window._wq ??= []
		window._wq.push({
			id,
			onReady,
		})
		window._wq.push((W: WistiaConsent) => {
			W.consent(tracking)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this effect to re‑run when `tracking` changes.
	}, [tracking])

	const handlePollingTick = (): void => {
		const vid = videoRef.current
		const watchedPercentage = (vid.time() / vid.duration()) * 100
		const numberOfTargets = progressTargetsRef.current.length

		const progressTargets = [...progressTargetsRef.current].filter(
			(targetPercentage) => {
				const hit = watchedPercentage >= targetPercentage
				if (hit) {
					pushToDataLayer({
						event: 'video_progress',
						video_percent: `${targetPercentage}%`,
						video_title: `${alt} (wistia id: ${id})`,
						video_url: `https://elancoanimalhealth.wistia.com/medias/${id}`,
						video_duration: vid.duration(),
					})
				}
				return !hit
			}
		)
		const progressMade = progressTargets.length < numberOfTargets
		if (progressMade) progressTargetsRef.current = progressTargets
	}

	useEffect(() => {
		const interval = setInterval(handlePollingTick, 2000)
		return () => {
			clearInterval(interval)
		}
	}, [])

	useEffect(() => {
		const handleVisibilityChange = (): void => {
			if (playSuspendedOffScreen) {
				if (document.hidden || !isActiveSlide) {
					videoRef.current.pause()
					pausedByVisibility.current = true
				} else if (pausedByVisibility.current) {
					pausedByVisibility.current = false
				}
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			document.removeEventListener(
				'visibilitychange',
				handleVisibilityChange
			)
		}
	}, [isActiveSlide, playSuspendedOffScreen])

	const iframeHeight = wistiaPodcast?.choice === 'podcast' ? '218px' : '100%'
	const embedOptions = [
		`playSuspendedOffScreen=${playSuspendedOffScreen}`,
		`autoPlay=${autoPlay ? '1' : '0'}`,
	].join('&')

	return (
		<>
			<Helmet>
				<script async src="//fast.wistia.net/assets/external/E-v1.js" />
			</Helmet>
			<div
				className={clsx(
					className,
					componentSpacing !== spacing?.none
						? 'container-wide'
						: 'w-full',
					'flex justify-center rounded-md py-4'
				)}
			>
				<div
					className={clsx(
						wistiaPodcast?.choice === 'podcast'
							? 'px-0 pb-0 pt-[20%]'
							: 'px-0 pb-0 pt-[52%]',
						'wistia_responsive_padding relative w-[inherit]'
					)}
				>
					<div className="wistia_responsive_wrapper absolute left-0 top-0 h-full w-full">
						<iframe
							allow="autoplay; fullscreen"
							allowFullScreen
							className="wistia_embed"
							frameBorder="0"
							height={iframeHeight}
							name="wistia_embed"
							scrolling="no"
							src={`https://fast.wistia.net/embed/iframe/${id}?videoFoam=true&${embedOptions}`}
							title={alt}
							width="100%"
						/>
					</div>
				</div>
			</div>
		</>
	)
}
